import { json, type ActionFunctionArgs } from '@remix-run/node';
import { z } from 'zod';
import { inviteToGroupChat } from '~/modules/chat/api/invite-to-group-chat.server';

export const INVITE_TO_GROUP_CHAT_API = '/remix-api/chat/invite-to-group-chat';

const FORM_DATA_SCHEMA = z.object({
    sourceConversationID: z.coerce.number().int().positive(),
    memberIDs: z.array(z.coerce.number().int().positive()).min(1),
    migrateMessagesFrom: z.coerce.number().int().nonnegative().nullish(),
});

/**
 * API endpoint to invite participants from a 1-on-1 chat to a group chat.
 * Finds or creates the group chat and optionally migrates (moves) messages.
 * Use with the `useUpdateConversation` hook.
 * @see {@link packages/web/app/modules/chat/hooks/use-update-conversation.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const payload = await request.json();
    const result = FORM_DATA_SCHEMA.safeParse(payload);

    if (!result.success) {
        logger.error(`Validation error in ${INVITE_TO_GROUP_CHAT_API}`, {
            errors: result.error.errors,
            payload,
        });
        return json(null, { status: 400 });
    }

    try {
        const response = await inviteToGroupChat({
            context,
            sourceConversationID: result.data.sourceConversationID,
            addMemberIDs: result.data.memberIDs,
            migrateMessagesFrom: result.data.migrateMessagesFrom,
        });

        if (!response) {
            return json(null, { status: 500 });
        }

        let statusCode = 200;
        if (
            response.__typename === 'NotAuthorizedError' ||
            response.__typename === 'NotFoundError'
        ) {
            statusCode = 403;
        } else if (response.__typename === 'UserError') {
            statusCode = 400;
        } else if (response.__typename === 'ServerError') {
            statusCode = 500;
        }

        return json(response, { status: statusCode });
    } catch (error) {
        logger.error(`Error in ${INVITE_TO_GROUP_CHAT_API}`, {
            error,
            data: result.data,
        });
        return json(null, { status: 500 });
    }
};
